/**
 * ArgumentMapView — LIM Level 2B: vertical flowchart with expandable steps.
 *
 * Closed state: each P shows a short summary (first clause, ~8-10 words).
 * Open state: tapped P expands to fill most of the viewport with full text
 * (scrollable if long, large font). Only previous and next P stay visible
 * as small compressed labels. All others hide.
 *
 * Obiezioni: collapsible group at bottom, each objection expandable.
 */

import { getDataService } from '@/services/data-registry';

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function cleanTermMarkers(s: string): string {
  return s.replace(/\{\{[^|]*\|([^}]*)\}\}/g, '$1');
}

function paragraphs(text: string): string {
  return text.split(/\n\n+/).map((p) => `<p>${esc(p.trim())}</p>`).join('');
}

/** Extract a short summary: first clause up to first comma/semicolon/dash, max ~60 chars */
function shortSummary(text: string): string {
  const clean = cleanTermMarkers(text);
  // Try splitting at first major punctuation
  const match = clean.match(/^(.{20,60}?)[,;:—–\-]/)
    || clean.match(/^(.{20,80}?)\.\s/);
  if (match) return match[1].trim();
  // Fallback: first 60 chars at word boundary
  if (clean.length <= 65) return clean;
  return clean.slice(0, 60).replace(/\s+\S*$/, '') + '…';
}

export async function mountArgumentMap(
  container: HTMLElement,
  argomentoId: string,
  locale: 'it' | 'en',
  onBack: () => void,
): Promise<void> {
  const data = getDataService();
  const arg = await data.getArgomento(argomentoId, locale);
  if (!arg) { container.innerHTML = '<p>Argomento non trovato</p>'; return; }

  const author = await data.getAuthor(arg.autore_id, locale);
  const authorName = author?.nome ?? arg.autore_id;
  const c = arg.content;
  const passo = c.argomento_passo_passo;

  // Build steps
  const allSteps: { id: string; num: string; summary: string; fullHtml: string; type: 'premise' | 'conclusion' }[] = [];

  for (const p of passo.premesse) {
    const enunciato = cleanTermMarkers(p.enunciato);
    const commento = cleanTermMarkers(p.commento);
    // LIM preview: prefer curated `sintesi` (≤120 chars, editorial).
    // Fallback to runtime truncation so pre-migration argomenti keep working.
    const summary = p.sintesi
      ? cleanTermMarkers(p.sintesi)
      : shortSummary(enunciato);
    allSteps.push({
      id: `p${p.numero}`,
      num: `P${p.numero}`,
      summary,
      fullHtml: `<div class="flow-full-enunciato">${esc(enunciato)}</div>${paragraphs(commento)}`,
      type: 'premise',
    });
  }

  // Conclusion
  const conclusionText = cleanTermMarkers(passo.conclusione);
  const conclusionSummary = passo.conclusione_sintesi
    ? cleanTermMarkers(passo.conclusione_sintesi)
    : shortSummary(conclusionText);
  allSteps.push({
    id: 'conclusion',
    num: '∴',
    summary: conclusionSummary,
    fullHtml: paragraphs(conclusionText),
    type: 'conclusion',
  });

  // Obiezioni — removed from map, kept only in desktop detail view

  // Render
  const enunciato = cleanTermMarkers(c.enunciato);
  const stepsHtml = allSteps.map((s) => `
    <div class="flow-step ${s.type === 'conclusion' ? 'flow-step--conclusion' : ''}" data-step="${s.id}">
      <div class="flow-step-closed">
        <span class="flow-step-num">${s.num}</span>
        <span class="flow-step-summary">${esc(s.summary)}</span>
      </div>
      <div class="flow-step-open">
        <div class="flow-step-open-header">
          <span class="flow-step-num">${s.num}</span>
          <span class="flow-step-summary-full">${esc(s.summary)}</span>
        </div>
        <div class="flow-step-open-body">${s.fullHtml}</div>
      </div>
    </div>
  `).join('');

  container.innerHTML = `
    <div class="argument-flow">
      <header class="argument-flow-header">
        <button type="button" class="map-back-btn" data-back>← ${esc(authorName)}</button>
        <div class="argument-flow-title-block">
          <h1 class="argument-flow-title">${esc(arg.nome)}</h1>
          <p class="argument-flow-enunciato">${esc(enunciato.length > 200 ? enunciato.slice(0, 200) + '…' : enunciato)}</p>
        </div>
      </header>
      <div class="argument-flow-steps">
        ${stepsHtml}
      </div>
    </div>
  `;

  // ── Wire interactions ──
  // All interactive items: steps + obiezioni group — unified behavior
  const stepEls = [...container.querySelectorAll<HTMLElement>('.flow-step')];
  const allBlocks = stepEls;
  let openBlock: HTMLElement | null = null;

  container.querySelector('[data-back]')!.addEventListener('click', onBack);

  // Step clicks
  stepEls.forEach((stepEl, idx) => {
    stepEl.addEventListener('click', () => {
      if (openBlock === stepEl) {
        closeAll();
      } else {
        expandBlock(stepEl, idx);
      }
    });
  });


  function expandBlock(block: HTMLElement, blockIdx: number): void {
    openBlock = block;
    allBlocks.forEach((el, i) => {
      el.classList.remove('is-expanded', 'is-compressed', 'is-neighbor');
      if (el === block) {
        el.classList.add('is-expanded');
      } else if (i === blockIdx - 1 || i === blockIdx + 1) {
        el.classList.add('is-neighbor');
      } else {
        el.classList.add('is-compressed');
      }
    });
    requestAnimationFrame(() => {
      block.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }

  function closeAll(): void {
    openBlock = null;
    allBlocks.forEach((el) => {
      el.classList.remove('is-expanded', 'is-compressed', 'is-neighbor');
    });
  }
}
